import {
  setDisplayErrorSnackBar,
  setIsLoading,
} from "../../../features/userSlice";
import { leadsManagementService } from "../../../services/api/leads-management.endpoints";
import store from "../../../store";

async function getLeadsContent(
  oauthTokenHeader: string,
  requestBody: {
    website_shared_uid: string[];
    date_from: string;
    date_to: string;
    page: number;
    page_size: number;
    search?: string;
    report_status?: string;
    brand_uid?: string[];
    qualified_by_brand?: boolean;
    lead_scoring_min?: number;
    has_duplicate_ip?: boolean;
  },
  setLeadsContent: any,
  trackPromise: any,
) {
  try {
    store.dispatch(setIsLoading(true));
    const response = await leadsManagementService.getLeadsReport(
      oauthTokenHeader,
      requestBody,
    );
    if (!response || response.status !== 200) {
      trackPromise(
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Something went wrong while getting the leads list",
            type: "error",
          }),
        ),
      );
    } else {
      setLeadsContent(response?.data);
    }
  } catch (e: any) {
    trackPromise(
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || "Unknown error occurred",
          type: "error",
        }),
      ),
    );
  }
}

export interface ReportLeadResult {
  leadId: number;
  succeeded: boolean;
}

/**
 * Reports the given lead ids to their brand partner(s). The endpoint reports
 * each lead individually and can return a mix of successes and failures in
 * the same 200 response (e.g. one brand's site blocked the request while
 * another accepted it), so this returns a per-lead result array rather than
 * a single boolean.
 *
 * Returns null only when the request itself failed (network/HTTP error) —
 * the error snackbar is already shown in that case. Callers should keep the
 * affected leads selected/reportable and let the caller decide what to do
 * next (e.g. leave the confirm dialog open, or let the user retry) rather
 * than assuming the report went through.
 */
async function reportLeadsToBrand(
  oauthTokenHeader: string,
  leadIds: number[],
  trackPromise: any,
): Promise<ReportLeadResult[] | null> {
  try {
    const response = await leadsManagementService.reportLeads(oauthTokenHeader, {
      lead_ids: leadIds,
    });
    if (!response || (response.status !== 200 && response.status !== 204)) {
      trackPromise(
        store.dispatch(
          setDisplayErrorSnackBar({
            message: "Something went wrong while reporting leads to brand",
            type: "error",
          }),
        ),
      );
      return null;
    }
    const results: any[] = response?.data?.data ?? [];
    return results.map((result) => ({
      leadId: result.lead_id,
      succeeded: String(result.status).toLowerCase() === "success",
    }));
  } catch (e: any) {
    trackPromise(
      store.dispatch(
        setDisplayErrorSnackBar({
          message: e?.parsedText || "Unknown error occurred while reporting leads to brand",
          type: "error",
        }),
      ),
    );
    return null;
  }
}

export const LeadsManagementEndPointLogic = {
  getLeadsContent,
  reportLeadsToBrand,
};
